// components/Kanban/GestionRooms.js
import { KeyboardSensor, MouseSensor, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";
import KanbanCard from "../../../../../components/Kanban/Card";
import Kanban from "../../../../../components/Kanban/Kanban";
import { getAllPermissions, removeRoomPermission, addRoomPermission } from "../../../store/userManagement/userManagementThunks";
import { getAllRoomsData } from "../../../../Map/store/MapDisplayThunks";

export default function ManageRooms({ selectedEmployeeAvatar, elementsPerPage = 100 }) {
    const [searchTerm, setSearchTerm] = useState("");

    const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 8 } });
    const mouseSensor = useSensor(MouseSensor);
    const touchSensor = useSensor(TouchSensor);
    const keyboardSensor = useSensor(KeyboardSensor);
    const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor, pointerSensor);
    const { mapConfig } = useSelector((state) => state.MapDisplay);
    const mapConfigRef = useRef({})

    const dispatch = useDispatch();

    const gestionRoomsConfig = {
        getData: async (config, selectedEmployeeAvatar, setters, localPermissions, permissions) => {
            const { setLoading, setColumns, setCards, setLocalCards } = setters;
            setLoading(true);
            setColumns([]);
            setCards([]);
            setLocalCards([]);

            let localRooms = [];
            let permissions2 = [];

            // Single API call - data gets transformed by setRoomsData
            const roomData = await dispatch(getAllRoomsData()).unwrap();

            if (!Array.isArray(roomData)) {
                return;
            }

            // Transform rooms data (after setRoomsData transform)
            roomData.forEach((room) => {
                localRooms.push({
                    pos: room.room_id,
                    id: room.label,
                    title: room.label,
                    room_id: room.room_id
                });
            });

            // Transform permissions from the already-transformed roomPermissions array
            roomData.forEach(room => {
                room.roomPermissions?.forEach((permission) => {
                    permissions2.push({
                        id: permissions2.length,
                        permission_id: permission.id,
                        name: permission.name,
                        room: room.room_id,
                    });
                });
            });
            return [localRooms, permissions2];
        },

        onCardDragEnd: async (config, permissions, permissionId, rooms, newRoom) => {
            const newRoomId = rooms.find((r) => r.id === newRoom).room_id;
            const newPerm = permissions.find((u) => u.id === permissionId);
            await dispatch(
                addRoomPermission({
                    params: [{ room_id: newRoomId, permission_id: newPerm.permission_id }],
                })
            );
            let localPermissions = [];
            if (newPerm) {
                localPermissions.push({
                    id: localPermissions.length !== 0 ? localPermissions.at(-1).id + 1 : 0,
                    permission_id: newPerm.permission_id,
                    name: newPerm.name,
                    room: newRoom.room_id,
                });
            }
            return permissions.concat(localPermissions);
        },

        filterColumns: (rooms, searchColumn) => {
            return rooms ? rooms.filter((room) => room.title?.toLowerCase().includes(searchColumn?.toLowerCase())) : [];
        },

        renderItem: (item, setIsSelected, selectedIds, columnIdPrefix) => {
            return <KanbanCard
                key={item.id}
                item={item}
                setIsSelected={setIsSelected}
                selectedIds={selectedIds}
                showContent={(localAvatar, perm) => (
                    <>
                        <p className="">{perm.name}</p>
                    </>
                )}
                columnIdPrefix={columnIdPrefix}
            />
        },

        renderItemFilter: (allItems, handleNewPerm, setIsSelected, selectedIds, columnIdPrefix, currentPage, elementsPerPage) =>
            allItems
                .slice((currentPage - 1) * elementsPerPage, currentPage * elementsPerPage)
                .map((perm) => {
                    return (
                        <KanbanCard
                            key={perm.id}
                            item={perm}
                            setIsSelected={setIsSelected}
                            selectedIds={selectedIds}
                            showContent={(localAvatar, p) => (
                                <>
                                    <p className="">{p.name}</p>
                                </>
                            )}
                            clickAction={() => handleNewPerm(perm)}
                            columnIdPrefix={columnIdPrefix}
                        />
                    );
                }),

        filterCards: (permissions, searchTerm, room) => {
            return permissions.filter((perm) => (
                perm.room === room.room_id &&
                perm.name !== undefined &&
                perm.name?.toLowerCase().includes(searchTerm?.toLowerCase())
            ));
        },

        filterAllCards: (allItems, items, column) => {
            return allItems
                .filter((u) => items.find((fu) => fu.permission_id === u.id && fu.room === column.id) === undefined)
        },

        getAllExternalItems: async (config, selectedEmployeeAvatar) => {
            const localPerms = await dispatch(getAllPermissions());
            const allPerms = localPerms?.payload || [];
            return allPerms;
        },

        onAddItem: async (config, permissions, perm, room) => {
            let localPermissions = [...permissions];
            await dispatch(
                addRoomPermission({
                    params: [{ permission_id: perm.id, room_id: room.room_id }],
                })
            );

            localPermissions.push({
                id: localPermissions.length !== 0 ? localPermissions.at(-1).id + 1 : 0,
                name: perm.name,
                permission_id: perm.id,
                room: room.room_id,
            });
            return localPermissions;
        },

        onDeleteItems: async (config, selectedIds, permissions, room) => {
            const apiCalls = selectedIds.map(async (id) => {
                const perm = permissions.find(u => u.id === id && u.room === room.room_id);
                if (perm) {
                    await dispatch(removeRoomPermission({
                        params: [{ permission_id: perm.permission_id, room_id: room.room_id }]
                    }));
                }
            });

            // Wait for all API calls to complete
            await Promise.all(apiCalls);

            // Create a new array by filtering out the deleted items
            const filteredPermissions = permissions.filter(u => !selectedIds.includes(u.id));

            return filteredPermissions;
        },

        dragOverlayShow: (activeId, permissions, setIsSelected, columnIdPrefix) => {
            if (!permissions) return <div>Empty</div>;
            return (
                <KanbanCard
                    key={activeId}
                    item={permissions.find((perm) => perm.id === activeId)}
                    setIsSelected={setIsSelected}
                    selectedIds={[]}
                    showContent={(localAvatar, perm) => (
                        <>
                            <p className="">{perm?.name.toUpperCase()}</p>
                        </>
                    )}
                    columnIdPrefix={columnIdPrefix}
                />
            );
        },
    }

    return (
        <>
            <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />

            <Kanban
                key={"gestion-rooms-kanban"}
                config={gestionRoomsConfig}
                sensors={sensors}
                searchColumn={searchTerm}
                selectedEmployeeAvatar={selectedEmployeeAvatar}
                columnIdPrefix="gestion-rooms"
                elementsPerPage={elementsPerPage}
            />
        </>
    );
};
